import type { PhotoMetadataRecord, PhotoRecord } from "@/lib/catalog"
import type { SortOption } from "@/lib/catalog/types"

type SortableMetadata = Pick<PhotoMetadataRecord, "dateCreated" | "dateModified">

export function sortPhotosByOption(
	photos: PhotoRecord[],
	sorting: SortOption,
	metadata: Record<string, SortableMetadata | undefined>,
	customOrder: Record<string, number>,
	now = Date.now(),
): PhotoRecord[] {
	const getCreationDate = (photo: PhotoRecord) => {
		const meta = metadata[photo.id]
		return meta?.dateCreated ?? meta?.dateModified ?? now
	}

	const getModifiedDate = (photo: PhotoRecord) => {
		const meta = metadata[photo.id]
		return meta?.dateModified ?? now
	}

	const compareFileNameAsc = (a: PhotoRecord, b: PhotoRecord) =>
		a.fileName.localeCompare(b.fileName)
	const compareFileNameDesc = (a: PhotoRecord, b: PhotoRecord) =>
		b.fileName.localeCompare(a.fileName)

	const sorted = [...photos]
	sorted.sort((a, b) => {
		switch (sorting) {
			case "filenameAscending":
				return compareFileNameAsc(a, b)
			case "filenameDescending":
				return compareFileNameDesc(a, b)
			case "dateAscending": {
				const diff = getCreationDate(a) - getCreationDate(b)
				return diff === 0 ? compareFileNameAsc(a, b) : diff
			}
			case "dateDescending": {
				const diff = getCreationDate(b) - getCreationDate(a)
				return diff === 0 ? compareFileNameDesc(a, b) : diff
			}
			case "modifiedDateAscending":
				return getModifiedDate(a) - getModifiedDate(b) || compareFileNameAsc(a, b)
			case "modifiedDateDescending":
				return getModifiedDate(b) - getModifiedDate(a) || compareFileNameDesc(a, b)
			case "customReordered":
				return (customOrder[a.id] ?? 0) - (customOrder[b.id] ?? 0)
			default:
				return 0
		}
	})

	return sorted
}
